import React, { useEffect, useState } from "react";
import { Box, TextField, Typography } from "@mui/material";
import CustomDialog from "components/Dialog/Dialog";
import { auditDashboardTheme } from "../../theme";

export type AuditDetailLottoPayoutDataDialogMode = "add" | "update";

export interface AuditDetailLottoPayoutDataFormValues {
  lottoSales: string;
  lottoPayout: string;
}

interface AuditDetailLottoPayoutDataDialogProps {
  open: boolean;
  mode: AuditDetailLottoPayoutDataDialogMode;
  initialValues?: AuditDetailLottoPayoutDataFormValues;
  isSaving?: boolean;
  onClose: () => void;
  onSave: (values: AuditDetailLottoPayoutDataFormValues) => void | Promise<void>;
}

const emptyValues: AuditDetailLottoPayoutDataFormValues = {
  lottoSales: "",
  lottoPayout: "",
};

const isValidAmountInput = (value: string) => value === "" || /^\d*(\.\d{0,2})?$/.test(value);

function AmountField({
  label,
  placeholder,
  value,
  onChange,
}: {
  label: string;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const fieldId = React.useId();

  return (
    <Box sx={{ display: "grid", gap: "6px" }}>
      <Typography
        component="label"
        htmlFor={fieldId}
        sx={{
          fontSize: 12,
          fontWeight: 800,
          color: auditDashboardTheme.colors.text,
        }}
      >
        {label}
      </Typography>
      <TextField
        id={fieldId}
        fullWidth
        type="number"
        variant="outlined"
        placeholder={placeholder}
        value={value}
        sx={{
          "& .MuiOutlinedInput-root": {
            minHeight: 48,
            "& fieldset": {
              borderColor: "#cbd5e1",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#2c6ca0 !important",
              borderWidth: "1px",
            },
          },
          "& .MuiOutlinedInput-input": {
            py: "12px",
          },
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          if (isValidAmountInput(nextValue)) {
            onChange(nextValue);
          }
        }}
        inputProps={{
          min: 0,
          step: "0.01",
          inputMode: "decimal",
          "aria-label": label,
        }}
      />
    </Box>
  );
}

export default function AuditDetailLottoPayoutDataDialog({
  open,
  mode,
  initialValues,
  isSaving = false,
  onClose,
  onSave,
}: AuditDetailLottoPayoutDataDialogProps) {
  const [values, setValues] = useState<AuditDetailLottoPayoutDataFormValues>(initialValues ?? emptyValues);
  const title = mode === "update" ? "Update Lotto/Payout Data" : "Add Lotto/Payout Data";

  useEffect(() => {
    if (open) {
      setValues(initialValues ?? emptyValues);
    }
  }, [initialValues, open]);

  const handleChange = (field: keyof AuditDetailLottoPayoutDataFormValues, nextValue: string) => {
    setValues((current) => ({
      ...current,
      [field]: nextValue,
    }));
  };

  return (
    <CustomDialog
      open={open}
      handleClose={onClose}
      handleSubmit={() => onSave(values)}
      title={title}
      buttonTxt="Save"
      cancelBtn
      cancelBtnTxt="Cancel"
      disabled={isSaving}
      isLoading={isSaving}
      width="420px"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "8px",
          maxWidth: "420px",
        },
      }}
    >
      <Box sx={{ display: "grid", gap: "16px", py: "4px" }}>
        <AmountField
          label="Lotto Sales($)"
          placeholder="Enter Lotto sales"
          value={values.lottoSales}
          onChange={(nextValue) => handleChange("lottoSales", nextValue)}
        />
        <AmountField
          label="Lotto Payout($)"
          placeholder="Enter lotto payout"
          value={values.lottoPayout}
          onChange={(nextValue) => handleChange("lottoPayout", nextValue)}
        />
      </Box>
    </CustomDialog>
  );
}
